import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useLocation, useNavigate } from "react-router-dom";
import FilterMotion from "./MotionForm/FilterMotion";

const labelTipo = (tipo = "", origen = "") => {
  const t = String(tipo || "").toUpperCase();
  const o = String(origen || "").toUpperCase();
  if (o === "INVENTARIO") {
    if (t === "ING") return "Ingreso Inventario";
    if (t === "EGR") return "Egreso Inventario";
    return "Inventario";
  }
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";
  if (t === "ING") return "Ingreso";
  if (t === "EGR") return "Egreso";
  return "";
};

const labelOrigen = (origen = "") => {
  const o = String(origen || "").toUpperCase();
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";
  if (o === "INVENTARIO") return "Inventario";
  return o;
};

const AdminMotion = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [origenFilter, setOrigenFilter] = useState("TODOS");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const fetchDocs = async () => {
    try {
      const r = await fetch("/core/movimientos/listar/", { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      setDocs(d.movimientos || []);
    } catch {
      setDocs([]);
      setSnackbar({ open: true, type: "error", message: "No se pudo cargar la lista" });
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("inventory_updated_snackbar");
    if (!raw) return;
    sessionStorage.removeItem("inventory_updated_snackbar");
    try {
      const s = JSON.parse(raw);
      if (s?.message) {
        setSnackbar({
          open: true,
          type: s.type || "success",
          message: s.message,
        });
        fetchDocs();
      }
    } catch {}
  }, []);

  useEffect(() => {
    const st = location.state && typeof location.state === "object" ? location.state : null;
    if (!st) return;

    const s = st.snackbar || st.snack || st.toast;
    const shouldRefresh = Boolean(st.refresh);

    if (s && s.message) {
      setSnackbar({
        open: true,
        type: s.type || "success",
        message: s.message,
      });
    }

    if (shouldRefresh) {
      fetchDocs();
    }

    if (s || shouldRefresh) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const filtered = useMemo(() => {
    const t = search.toLowerCase().trim();

    return (docs || []).filter((d) => {
      const origen = String(d.origen || "").toUpperCase();
      const tipo = String(d.tipo || "").toUpperCase();

      let okOrigen = true;

      switch (String(origenFilter || "TODOS").toUpperCase()) {
        case "COMPRA":
          okOrigen = origen === "COMPRA";
          break;
        case "VENTA":
          okOrigen = origen === "VENTA";
          break;
        case "INVENTARIO_ING":
          okOrigen = origen === "INVENTARIO" && tipo === "ING";
          break;
        case "INVENTARIO_EGR":
          okOrigen = origen === "INVENTARIO" && tipo === "EGR";
          break;
        default:
          okOrigen = true;
      }

      const okSearch =
        !t ||
        String(d.codigo || "").toLowerCase().includes(t) ||
        String(d.fecha || "").toLowerCase().includes(t) ||
        String(d.descripcion || "").toLowerCase().includes(t) ||
        String(d.responsable || "").toLowerCase().includes(t);

      const fecha = String(d.fecha || "");
      const okDateFrom = !dateFrom || (fecha && fecha >= dateFrom);
      const okDateTo = !dateTo || (fecha && fecha <= dateTo);

      return okOrigen && okSearch && okDateFrom && okDateTo;
    });
  }, [docs, search, origenFilter, dateFrom, dateTo]);

  useEffect(() => {
    setPage(0);
  }, [search, origenFilter, dateFrom, dateTo]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", backgroundColor: "#1565C0" }}
              onClick={() => navigate("/admin-panel/inventario/movimientos/nuevo")}
            >
              Agregar Movimiento
            </Button>
          </Box>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ mb: 2, fontWeight: 600 }}>Movimientos</Typography>

            <FilterMotion
              search={search}
              setSearch={setSearch}
              origenFilter={origenFilter}
              setOrigenFilter={setOrigenFilter}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Fecha</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Descripción</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Responsable</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{d.fecha}</TableCell>

                        <TableCell>
                          <Link
                            href={`/admin-panel/inventario/movimientos/${d.id}`}
                            target="_blank"
                            underline="hover"
                            sx={{ fontWeight: 600, display: "inline-flex", cursor: "pointer" }}
                          >
                            {d.codigo}
                          </Link>

                          <Typography sx={{ fontSize: 11, color: "#757575", mt: 0.25 }}>
                            {labelTipo(d.tipo, d.origen)} / {labelOrigen(d.origen)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={d.descripcion || ""}
                        >
                          {d.descripcion || "-"}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ color: "#424242" }}>
                            {d.responsable || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay movimientos registrados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminMotion;
